<script setup lang="ts">
import { computed, ref } from 'vue'

import clsx from 'clsx'
import draggable from 'vuedraggable'

import BetaBaseImage from '@/components/beta-editor/BetaBaseImage.vue'
import modal from '@/services/modal'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorDefaultStore } from '@/stores/editor/default'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'
import { useMediaManagerStore } from '@/stores/modal/media-manager'

const props = defineProps<{
    element: any
    elementIndex: number
    sectionIndex: number
    sectionBackgroundColor: string
    elementParent?: any
    schema?: any
}>()

const betaStore = useEditorBetaStore()
const defaultStore = useEditorDefaultStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()
const mediaManagerStore = useMediaManagerStore()

const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const content = computed(() => Array.isArray(props.element.frameContent) ? props.element.frameContent : [])
const editingIndex = ref<number | null>(null)
const draftComment = ref<any>({})
const isCustomParentBackgroundColor = computed(() => props.elementParent?.extra?.background_color && props.elementParent?.extra?.background_color !== '')
const backgroundColor = computed(() => {
    if (isCustomParentBackgroundColor.value) return props.elementParent.extra.background_color

    return editorPageStore.getSectionExtra(props.sectionIndex)?.background_color || props.sectionBackgroundColor
})
const textColor = computed(() => layoutStore.getAdaptiveTextColor(backgroundColor.value))
const dynamicStyle = computed(() => ({ color: textColor.value, stroke: textColor.value }))

const updateComments = (comments: any[]) => {
    betaStore.patchElementBySchema(props.schema, {
        frameContent: [...comments],
    })
}

const openEditComment = (index: number) => {
    editingIndex.value = index
    draftComment.value = {
        ...(content.value[index]?.frameContent ?? {}),
    }
}

const openNewComment = () => {
    editingIndex.value = content.value.length
    draftComment.value = {
        name: '',
        picture: '',
        text: '',
    }
}

const onAddImage = () => {
    mediaManagerStore.setMaxSelectedMedias(1)
    mediaManagerStore.setIsOpenModal(true)
    mediaManagerStore.setAction((media: any) => {
        const selectedMedia = Array.isArray(media) ? media[0] : media
        if (!selectedMedia) return

        draftComment.value = {
            ...draftComment.value,
            picture: selectedMedia.content,
        }
    })
}

const onSaveComment = () => {
    if (editingIndex.value === null) return

    const comments = [...content.value]
    comments[editingIndex.value] = {
        ...defaultStore.newElementComment,
        ...(comments[editingIndex.value] ?? {}),
        frameContent: {
            ...defaultStore.newElementComment.frameContent,
            ...draftComment.value,
        },
    }

    updateComments(comments)
    editingIndex.value = null
    draftComment.value = {}
}

const onRemoveComment = async (index: number) => {
    const result = await modal.confirmation('Confirmar Exclusão', 'Deseja realmente excluir este depoimento?', {
        type: 'danger',
        textConfirm: 'Sim, excluir',
        textCancel: 'Não, voltar',
    })

    if (!result) return

    const comments = [...content.value]
    comments.splice(index, 1)
    updateComments(comments)
}

const onEndDraggable = () => {
    updateComments([...content.value])
}
</script>

<template>
    <div
        tabindex="0"
        :id="'betaEditorComments-' + elementIndex"
        :class="clsx('editor-comments-beta')"
        :data-active="isActive"
        :schema="JSON.stringify(schema)"
        :style="dynamicStyle"
    >
        <div class="space-y-6 p-4">
            <h2 class="text-lg font-bold">Depoimentos</h2>

            <draggable :list="content" item-key="index" handle=".handle-drag" class="space-y-6" @change="onEndDraggable">
                <template #item="{ element, index }">
                    <div class="rounded-lg border-[1.5px] border-[#F5F5F5] p-6">
                        <div class="flex w-full justify-between">
                            <div class="flex items-center space-x-4">
                                <button class="handle-drag cursor-ns-resize" type="button">
                                    <svg class="h-8 w-8 stroke-[var(--main-color)]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                        <path d="M15.6 15.6L12 19.2L8.39999 15.6M8.39999 8.39999L12 4.79999L15.6 8.39999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </button>

                                <beta-base-image :src="element.frameContent.picture" class-name="h-10 w-10 rounded-full object-cover" />

                                <span>{{ element.frameContent.name }}</span>
                            </div>

                            <div class="space-x-3">
                                <button type="button" @click="openEditComment(index)">
                                    <svg :style="dynamicStyle" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor">
                                        <path d="M9.61536 15H15M1 15L4.91821 14.1845C5.12621 14.1412 5.3172 14.0355 5.4672 13.8804L14.2385 4.8157C14.659 4.38109 14.6587 3.67661 14.2378 3.24237L12.3798 1.32535C11.9591 0.891291 11.2774 0.891587 10.857 1.32601L2.08479 10.3917C1.93509 10.5464 1.83287 10.7433 1.79092 10.9577L1 15Z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </button>

                                <button type="button" @click="onRemoveComment(index)">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                                        <path d="M0.888885 3.82353H15.1111M5.33333 1H10.6667M11.1111 17H4.88889C3.90705 17 3.11111 16.1572 3.11111 15.1176L2.70524 4.80389C2.6842 4.26918 3.08792 3.82353 3.59336 3.82353H12.4066C12.9121 3.82353 13.3158 4.26918 13.2948 4.80389L12.8889 15.1176C12.8889 16.1572 12.0929 17 11.1111 17Z" stroke="#EB4D31" stroke-width="1.5" stroke-linecap="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <div class="ml-12 mt-3" v-html="element.frameContent.text" />
                    </div>
                </template>
            </draggable>

            <button type="button" class="flex items-center justify-center rounded-full border-2 border-[var(--main-color)] px-6 py-3" @click="openNewComment">
                <span class="mr-2 text-base font-bold text-[var(--main-color)]">Novo Depoimento</span>
                <svg class="h-6 w-6 stroke-[var(--main-color)]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 7.19995L12 16.8M16.8 12L7.2 12" stroke-width="2" stroke-linecap="round" />
                </svg>
            </button>

            <div v-if="editingIndex !== null" class="rounded-2xl border border-[#DCE5F2] bg-white p-4">
                <div class="grid gap-3">
                    <label class="grid gap-1 text-sm font-semibold">
                        Nome
                        <input v-model="draftComment.name" class="rounded border border-[#DCE5F2] px-3 py-2 outline-none">
                    </label>

                    <label class="grid gap-1 text-sm font-semibold">
                        Texto
                        <textarea v-model="draftComment.text" class="min-h-[120px] rounded border border-[#DCE5F2] px-3 py-2 outline-none" />
                    </label>

                    <div class="flex items-center gap-3">
                        <beta-base-image v-if="draftComment.picture" :src="draftComment.picture" class-name="h-12 w-12 rounded-full object-cover" />
                        <button type="button" class="rounded-full border border-[#BFD2EC] px-4 py-2 text-sm font-semibold text-[#315A9A]" @click="onAddImage">
                            Escolher imagem
                        </button>
                    </div>

                    <div class="flex justify-end gap-2">
                        <button type="button" class="rounded-full px-4 py-2 text-sm font-semibold text-[#6C7E99]" @click="editingIndex = null">
                            Cancelar
                        </button>
                        <button type="button" class="rounded-full bg-[var(--main-color)] px-4 py-2 text-sm font-bold text-white" @click="onSaveComment">
                            Salvar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.editor-comments-beta {
    @apply relative my-3 border-2 border-transparent bg-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
}

.editor-comments-beta[data-active='true'] {
    @apply border-[#8EB3E8] shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}
</style>
